/**
 * Compute dirty state by comparing current form data with original data
 * 
 * Returns only the changed fields as a partial object.
 * This ensures we only send changed fields in the update payload.
 * 
 * @param original - Original data from API
 * @param current - Current form state
 * @returns Object containing only changed fields
 */
export function computeDirtyState<T extends Record<string, any>>(
  original: T,
  current: T
): Partial<T> {
  const changes: Partial<T> = {};

  // Check all keys in current state
  for (const key in current) {
    const originalValue = original[key];
    const currentValue = current[key];

    // Deep comparison for objects/arrays
    if (JSON.stringify(originalValue) !== JSON.stringify(currentValue)) {
      changes[key] = currentValue;
    }
  }

  return changes;
}

/**
 * Check if form has any changes (dirty state)
 * 
 * @param original - Original data from API
 * @param current - Current form state
 * @returns true if there are changes, false otherwise
 */
export function isDirty<T extends Record<string, any>>(
  original: T,
  current: T
): boolean {
  const changes = computeDirtyState(original, current);
  return Object.keys(changes).length > 0;
}

